import type { IconName } from "@/components/admin/icon";
import { MARKETING_PATHS } from "@/config/marketing-nav";

export interface AdminNavItem {
	k: string;
	l: string;
	icn: IconName;
	path: string;
	count?: number;
	alert?: boolean;
	/** Préfixes d'URL supplémentaires qui gardent l'entrée active (onglets sur d'autres chemins). */
	also?: string[];
}

export interface AdminNavGroup {
	group: string;
	items: AdminNavItem[];
}

export interface AdminUser {
	name: string;
	role: string;
	initials: string;
	email: string;
	tenant: string;
}

export const ADMIN_USER: AdminUser = {
	name: "Kevin Wamba",
	role: "Super Admin",
	initials: "KW",
	email: "kevin@akwaba.cm",
	tenant: "Akwaba Commerce SA",
};

export const ADMIN_NAV: AdminNavGroup[] = [
	{
		group: "Vue d'ensemble",
		items: [
			{
				k: "home",
				l: "Tableau de bord",
				icn: "home",
				path: "/",
				also: ["/reporting"],
			},
		],
	},
	{
		group: "Commerce",
		items: [
			{
				k: "orders",
				l: "Commandes",
				icn: "cart",
				path: "/commandes",
				also: ["/retours"],
			},
			{
				k: "catalog",
				l: "Catalogue",
				icn: "box",
				path: "/catalogue",
				also: ["/categories", "/marques", "/attributs", "/avis-produits"],
			},
			{ k: "customers", l: "Clients", icn: "users", path: "/clients" },
		],
	},
	{
		group: "Opérations",
		items: [
			{
				k: "logistics",
				l: "Logistique",
				icn: "truck",
				path: "/logistique",
			},
			{
				k: "suppliers",
				l: "Fournisseurs",
				icn: "factory",
				path: "/fournisseurs",
			},
			{
				k: "support",
				l: "Support CSR",
				icn: "chat",
				path: "/support",
				alert: true,
			},
			{ k: "quotes", l: "Demandes de devis", icn: "tag", path: "/devis" },
		],
	},
	{
		group: "Finance",
		items: [
			{ k: "payments", l: "Paiements", icn: "card", path: "/paiements" },
			{ k: "finance", l: "Comptabilité", icn: "bank", path: "/finance" },
		],
	},
	{
		group: "Marketing",
		items: [
			/**
			 * Une seule entrée pour dix-sept écrans.
			 *
			 * Ils vivaient à huit endroits de cette barre, plus deux sous
			 * « Commerce » : on préparait une promotion dans un coin, la bannière
			 * qui l'annonce dans un autre, le message qui la pousse dans un
			 * troisième, sans jamais voir les trois ensemble. La colonne de
			 * sous-navigation du module les tient tous, groupés par famille.
			 */
			{
				k: "marketing",
				l: "Marketing & Contenus",
				icn: "layout",
				path: "/marketing",
				also: MARKETING_PATHS,
			},
		],
	},
	{
		group: "Système",
		items: [
			{
				k: "admin",
				l: "Administration",
				icn: "shield",
				path: "/administration",
			},
			{
				k: "notifications",
				l: "Notifications",
				icn: "bell",
				path: "/notifications",
			},
			{
				k: "hubs",
				l: "Hubs logistiques",
				icn: "pin",
				path: "/parametres/hubs",
			},
		],
	},
];

const STATIC_CRUMBS: Record<string, [string, string]> = {
	"/": ["Vue d'ensemble", "Tableau de bord"],
	"/notifications": ["Système", "Notifications"],
	"/reporting": ["Vue d'ensemble", "Tableau de bord → Reporting"],
	"/commandes": ["Commerce", "Commandes"],
	"/commandes/nouvelle": ["Commerce", "Commandes → Nouvelle"],
	"/commandes/recherche": ["Commerce", "Commandes → Recherche avancée"],
	"/commandes/statistiques": ["Commerce", "Commandes → Statistiques"],
	"/retours": ["Commerce", "Commandes → Retours (RMA)"],
	"/catalogue": ["Commerce", "Catalogue"],
	"/catalogue/nouveau": ["Commerce", "Catalogue → Nouveau produit"],
	"/catalogue/revue": ["Commerce", "Catalogue → Revue"],
	"/categories": ["Commerce", "Catalogue → Catégories"],
	"/marques": ["Commerce", "Catalogue → Marques"],
	"/attributs": ["Commerce", "Catalogue → Attributs"],
	"/catalogue/import-export": ["Commerce", "Catalogue → Import / Export"],
	"/catalogue/medias": ["Commerce", "Catalogue → Bibliothèque d'images"],
	"/promotions": ["Marketing", "Promotions"],
	"/njangi": ["Marketing", "Njangi"],
	"/avis-produits": ["Commerce", "Catalogue → Avis produits"],
	"/clients": ["Commerce", "Clients"],
	"/clients/nouveau": ["Commerce", "Clients → Nouveau"],
	"/marketing/segments": ["Marketing", "Segments d’audience"],
	"/clients/fidelite": ["Commerce", "Clients → Fidélité"],
	"/clients/parrainage": ["Commerce", "Clients → Parrainage"],
	"/clients/rgpd": ["Commerce", "Clients → RGPD"],
	"/clients/statistiques": ["Commerce", "Clients → Statistiques"],
	"/logistique": ["Opérations", "Logistique"],
	"/logistique/zones": ["Opérations", "Zones de livraison"],
	"/fournisseurs": ["Opérations", "Fournisseurs & cautions"],
	"/support": ["Opérations", "Support CSR"],
	"/devis": ["Opérations", "Demandes de devis"],
	"/catalogue/assistant-ia": ["Commerce", "Catalogue → Assistant IA"],
	"/paiements": ["Finance", "Paiements"],
	"/paiements/momo-live": ["Finance", "Paiements → MoMo Live"],
	"/paiements/remboursements": ["Finance", "Paiements → Remboursements"],
	"/paiements/rapprochement": ["Finance", "Paiements → Rapprochement"],
	"/paiements/litiges": ["Finance", "Paiements → Litiges"],
	"/paiements/prestataires": ["Finance", "Paiements → Prestataires"],
	"/paiements/cod": ["Finance", "Paiements → COD"],
	"/finance": ["Finance", "Comptabilité"],
	"/finance/factures": ["Finance", "Comptabilité → Factures"],
	"/finance/factures/nouvelle": ["Finance", "Comptabilité → Nouvelle facture"],
	"/finance/avoirs": ["Finance", "Comptabilité → Avoirs"],
	"/finance/journaux": ["Finance", "Comptabilité → Journaux"],
	"/finance/plan-comptable": ["Finance", "Comptabilité → Plan comptable"],
	"/finance/tva": ["Finance", "Comptabilité → Déclaration TVA"],
	"/finance/exports": ["Finance", "Comptabilité → Exports"],
	"/finance/etats": ["Finance", "Comptabilité → États & clôture"],
	"/finance/budgets": ["Finance", "Comptabilité → Budgets"],
	"/finance/audit": ["Finance", "Comptabilité → Audit & contrôles"],
	"/marketing": ["Marketing", "Vue d’ensemble"],
	"/marketing/bannieres": ["Marketing", "Bannières"],
	"/marketing/mag": ["Marketing", "Le Mag"],
	"/marketing/newsletters": ["Marketing", "Newsletters"],
	"/marketing/push": ["Marketing", "Notifications de l’app"],
	"/marketing/whatsapp": ["Marketing", "WhatsApp Business"],
	"/marketing/calendrier": ["Marketing", "Calendrier éditorial"],
	"/cms": ["Marketing", "Page d’accueil"],
	"/cms/pages": ["Marketing", "Pages d’information"],
	"/cms/engagements": ["Marketing", "Engagements commerciaux"],
	"/cms/referencement": ["Marketing", "Référencement"],
	"/reseaux-sociaux": ["Marketing", "Réseaux sociaux"],
	"/publicites": ["Marketing", "Publicités"],
	"/marketing/generateur": ["Marketing", "Générateur IA"],
	"/liens-courts": ["Marketing", "Liens courts"],
	"/liens-courts/statistiques": ["Marketing", "Liens courts → Statistiques"],
	"/parametres/lancement": ["Marketing", "Lancement / Site"],
	"/administration": ["Système", "Administration"],
	"/parametres/hubs": ["Système", "Hubs logistiques"],
	"/profil": ["Compte", "Mon profil"],
	"/profil/securite": ["Compte", "Sécurité"],
	"/profil/sessions": ["Compte", "Sessions"],
	"/profil/preferences": ["Compte", "Préférences"],
	"/profil/securite/2fa/methode": ["Compte", "Sécurité → Ajouter 2FA"],
	"/profil/securite/2fa/totp": ["Compte", "Sécurité → Setup TOTP"],
	"/profil/securite/2fa/recovery-codes": [
		"Compte",
		"Sécurité → Codes de récupération",
	],
};

export function getCrumbs(pathname: string): [string, string] {
	if (STATIC_CRUMBS[pathname]) return STATIC_CRUMBS[pathname];

	// Patterns avec paramètres dynamiques
	if (/^\/catalogue\/[^/]+\/modifier$/.test(pathname)) {
		return ["Commerce", "Catalogue → Modifier produit"];
	}
	if (/^\/catalogue\/[^/]+$/.test(pathname)) {
		return ["Commerce", "Catalogue → Fiche produit"];
	}
	if (/^\/commandes\/[^/]+$/.test(pathname)) {
		return ["Commerce", "Commandes → Détail"];
	}
	if (/^\/liens-courts\/[^/]+$/.test(pathname)) {
		return ["Marketing", "Liens courts → Détail"];
	}
	if (/^\/clients\/[^/]+$/.test(pathname)) {
		return ["Commerce", "Clients → Profil"];
	}

	return ["", ""];
}
